/**
 * Pipeline runs — request, approve, run now, cancel, list, get, and the daily
 * schedule · CLM-83 (R-83-1, Gerry, 14 Sep 2026: "it should be all controlled
 * from the pipeline run page"; D-83-1 … D-83-17).
 *
 * A run is an **org-level** object: it carries no `root_contract_key` and no
 * scope predicate applies to it (D-83-14). The gate is the action —
 * `pipeline:request` (contributor+) for request / cancel, `pipeline:approve`
 * (admin) for approve / run-now / schedule, `contract:read` for the two reads
 * (NFR-06, D-83-2, D-83-3, D-83-15). Every write is one `clm.audit_log` row
 * through `tg_pipeline_run_audit` / `tg_pipeline_schedule_audit` (0092, NFR-07).
 *
 * ## The filter
 *
 * `limit` (1..500, default 25) and `contract_key` reach the runner
 * (`PipelineRunOptions`). `contract_type` and `tags` are accepted so the form
 * matches the Pipeline page's own filters, but **the worker ignores them today**
 * — the runner has no option for either (CLM-85 candidate); the response echoes
 * the filter back unchanged so the row records what was asked.
 *
 * ## One queue slot (D-83-5)
 *
 * At most one `requested` / `approved` / `running` row exists at a time —
 * `ux_pipeline_run_one_active` (0092). A request or run-now while one is pending
 * is `409 conflict` with `run_already_pending` in `field_errors[0].message`.
 */
import { z } from 'zod';
import { ContractKeySchema, IsoTimestampUtcSchema } from '../primitives.js';
import { CONTRACT_TYPES } from '../vocabulary.js';
import { TagSchema } from './contract-tags.js';
export const PIPELINE_RUN_STATUSES = ['requested', 'approved', 'running', 'completed', 'failed', 'cancelled'];
/** The statuses a run may still leave; everything else is terminal. */
export const PIPELINE_RUN_ACTIVE_STATUSES = ['requested', 'approved', 'running'];
export const PIPELINE_RUN_SOURCES = ['manual', 'schedule'];
export const PIPELINE_RUN_LIMIT_DEFAULT = 25;
export const PIPELINE_RUN_LIMIT_MAX = 500;
export const PIPELINE_RUN_REASON_MAX_LENGTH = 400;
/** The 409 the one-slot rule produces (D-83-5); rides in `field_errors[0].message`. */
export const RUN_ALREADY_PENDING = 'run_already_pending';
export const PipelineRunFilterSchema = z
    .object({
    /** Accepted for the form; not a runner option today (see the module note). */
    contract_type: z.enum(CONTRACT_TYPES).optional(),
    contract_key: ContractKeySchema.optional(),
    /** Accepted for the form; not a runner option today (see the module note). */
    tags: z.array(TagSchema).max(10).optional(),
    limit: z.number().int().min(1).max(PIPELINE_RUN_LIMIT_MAX).default(PIPELINE_RUN_LIMIT_DEFAULT),
})
    .strict();
export const PipelineRunCountsSchema = z
    .object({
    seen: z.number().int().min(0),
    written: z.number().int().min(0),
    held: z.number().int().min(0),
    failed: z.number().int().min(0),
})
    .strict();
export const PipelineRunSchema = z
    .object({
    pipeline_run_id: z.string().uuid(),
    source: z.enum(PIPELINE_RUN_SOURCES),
    status: z.enum(PIPELINE_RUN_STATUSES),
    requested_by_upn: z.string(),
    requested_utc: IsoTimestampUtcSchema,
    reason: z.string().max(PIPELINE_RUN_REASON_MAX_LENGTH).nullable(),
    filter: PipelineRunFilterSchema,
    approved_by_upn: z.string().nullable(),
    approved_utc: IsoTimestampUtcSchema.nullable(),
    started_utc: IsoTimestampUtcSchema.nullable(),
    heartbeat_utc: IsoTimestampUtcSchema.nullable(),
    finished_utc: IsoTimestampUtcSchema.nullable(),
    counts: PipelineRunCountsSchema,
    /** Handed to the runner so its `ai_action` rows join the approving admin (CR-01). */
    correlation_id: z.string().uuid(),
    error_summary: z.string().nullable(),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* request-pipeline-run · run-pipeline-now                                     */
/* -------------------------------------------------------------------------- */
export const RequestPipelineRunRequestSchema = z
    .object({
    filter: PipelineRunFilterSchema.default({ limit: PIPELINE_RUN_LIMIT_DEFAULT }),
    reason: z.string().trim().min(1).max(PIPELINE_RUN_REASON_MAX_LENGTH),
})
    .strict();
export const RequestPipelineRunResponseSchema = z.object({ run: PipelineRunSchema }).strict();
/** Same body as a request; the admin gate makes it create **and** approve in one action (D-83-3). */
export const RunPipelineNowRequestSchema = RequestPipelineRunRequestSchema;
export const RunPipelineNowResponseSchema = RequestPipelineRunResponseSchema;
/* -------------------------------------------------------------------------- */
/* approve · cancel · get — keyed by the path parameter (D-83-8)               */
/* -------------------------------------------------------------------------- */
export const RunIdRequestSchema = z.object({ pipeline_run_id: z.string().uuid() }).strict();
export const ApprovePipelineRunResponseSchema = RequestPipelineRunResponseSchema;
export const CancelPipelineRunResponseSchema = RequestPipelineRunResponseSchema;
export const GetPipelineRunResponseSchema = RequestPipelineRunResponseSchema;
/* -------------------------------------------------------------------------- */
/* list-pipeline-runs                                                          */
/* -------------------------------------------------------------------------- */
export const PIPELINE_RUN_HISTORY_LIMIT = 20;
export const ListPipelineRunsRequestSchema = z.object({}).strict();
export const ListPipelineRunsResponseSchema = z
    .object({
    /** The one `running` row, or null. */
    active: PipelineRunSchema.nullable(),
    /** `requested` / `approved` rows — at most one under D-83-5, two admitted for the moment a claim is in flight. */
    pending: z.array(PipelineRunSchema).max(2),
    /** The last twenty terminal rows, `finished_utc DESC`. */
    history: z.array(PipelineRunSchema).max(PIPELINE_RUN_HISTORY_LIMIT),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* get-pipeline-schedule · set-pipeline-schedule                               */
/* -------------------------------------------------------------------------- */
export const PipelineScheduleSchema = z
    .object({
    enabled: z.boolean(),
    hour_utc: z.number().int().min(0).max(23),
    batch_limit: z.number().int().min(1).max(PIPELINE_RUN_LIMIT_MAX),
    /** The admin who last saved the schedule — and the recorded approver of every run it produces (D-83-4). */
    updated_by_upn: z.string().nullable(),
    updated_utc: IsoTimestampUtcSchema.nullable(),
})
    .strict();
export const GetPipelineScheduleRequestSchema = z.object({}).strict();
export const GetPipelineScheduleResponseSchema = z.object({ schedule: PipelineScheduleSchema }).strict();
export const SetPipelineScheduleRequestSchema = PipelineScheduleSchema.pick({
    enabled: true,
    hour_utc: true,
    batch_limit: true,
}).strict();
export const SetPipelineScheduleResponseSchema = GetPipelineScheduleResponseSchema;
//# sourceMappingURL=pipeline-runs.js.map